import { useTranslation } from "react-i18next";
import type {
  Assets,
  AssetsSummary,
  PIMProduct,
  PIMProductBase,
  SupportedAssetAccessibility,
  SupportedAssetCategoryType,
  TdsGeneratedAssetSchema,
  PIMProductStatusAck,
} from "../../../../../types/types.PIM";
import { H6Bold } from "../../../../../components/Typography/Typography";
import { Form } from "../../../../../layout/FormLayout";
import { Controller } from "react-hook-form";
import styled from "styled-components";
import { Table } from "../../../../../components/Table/Table";
import {
  ButtonContainer,
  PrimaryButtonLargeV2,
  TertiaryButtonLargeV2,
} from "../../../../../components/Buttons/Buttons";
import { SelectBoxV2 } from "../../../../../components/SelectBoxV2/SelectBoxV2";
import { GenerateDocument } from "./GenerateDocument";
import { TextField } from "../../../../../components/TextFields/TextFields";
import {
  TablePlaceholder,
  toTitleCase,
  useFormWrapper,
  useStoreState,
  useUpdateProductStatus,
} from "../../../../../util/util";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import type { AxiosError } from "axios";
import { endpoints } from "../../../../../endpoints";
import { strings } from "../../../../../util/strings";
import type { OptionType, SupportedLanguage } from "../../../../../types/types";
import axios from "axios";
import { useNotifications } from "../../../../../components/Notifications/NotificationsContext";
import type { Getter } from "@tanstack/react-table";
import { DocumentAccessibility } from "../../../../../components/DocumentAccessibility/DocumentAccessibility";
import {
  accessibilityRequestData,
  disableAccessibilitySubmit,
  getCustomersExistingData,
  getRolesExistingData,
  getUsersExistingData,
} from "../util";
import useSWR from "swr";
import { AssetContainerType } from "../../../../../util/assetContainer.types";
import {
  getActiveGeneratedAssetForCategory,
  getActiveTemplateGeneratedAssets,
  useTemplateGeneratedAssets,
} from "../../hooks/useTemplateGeneratedAssets";

const ScrollArea = styled.div`
  max-height: 460px;
  overflow-y: auto;
  display: block;
`;

export const EditOrGenerateProductDocument = forwardRef<
  { clear_poll_timeout: () => void },
  {
    asset?: AssetsSummary;
    product?: PIMProductBase;
    replaceProductId?: (id: string) => void;
    onSuccess: (productId?: string, shouldFetchData?: boolean) => void;
    onCancel: () => void;
    assetContainerType?: AssetContainerType;
    isCategoryDisabled?: boolean;
  }
>((props, ref) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { assetContainerType = "product" } = props;
  const { notifySuccess, notifyError } = useNotifications();
  const [tableData, setTableData] = useState<
    { name: string; type: "group" | "collection" }[]
  >([]);
  const [asset, setAsset] = useState<Assets>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [generatedAsset, setGeneratedAsset] =
    useState<TdsGeneratedAssetSchema>();
  const [productOptions, setProductOptions] = useState<OptionType<string>[]>();
  const [poll_timeout, set_poll_timeout] = useState<NodeJS.Timeout>();
  const [accessibility, setAccessibility] =
    useState<SupportedAssetAccessibility>(asset?.visibility || "internal");
  const templateId =
    props.product && "product_schema" in props.product
      ? (props.product as PIMProduct).product_schema.template_id
      : undefined;
  const { data: templateGeneratedAssets } =
    useTemplateGeneratedAssets(templateId);

  const assetCategories = useMemo<OptionType<SupportedAssetCategoryType>[]>(
    () =>
      getActiveTemplateGeneratedAssets(templateGeneratedAssets).map(
        (category) => ({
          label: category.asset_category || "",
          value: category.category_id || "",
        })
      ),
    [templateGeneratedAssets]
  );

  const mapSectionsToTableData = useCallback(
    (
      sections: TdsGeneratedAssetSchema["sections"]
    ): { name: string; type: "group" | "collection" }[] => {
      return sections.map((section) => ({
        name:
          section.group_type === "group"
            ? section.group.display_name
              ? t([section.group.display_name])
              : section.group.name
            : section.collection.display_name
            ? t([section.collection.display_name])
            : section.collection.name,
        type: section.group_type,
      }));
    },
    [t]
  );

  useSWR<Assets, AxiosError>(
    props.asset?.is_generated && !asset
      ? `${endpoints.v2_tenants_id_pim_assets_id(
          tenant_id,
          props.asset.id
        )}?is_generated=true`
      : null,
    {
      onSuccess: (data) => {
        if (data.is_generated) {
          onGenerateAsset(data);
          setGeneratedAsset(data.tds_generated_asset);
          if (!tableData.length) {
            setTableData(
              mapSectionsToTableData(data.tds_generated_asset.sections)
            );
          }
          if (!productOptions) {
            getProducts(data);
          }
        }
      },
      onError: (error) => {
        notifyError(t("There was an error fetching the generated asset"), {
          error,
        });
      },
    }
  );

  const updateProductStatus = useUpdateProductStatus({
    product: props.product
      ? props.product
      : ({ status: "archived" } as PIMProduct),
  });

  const defaultValues = useMemo(() => {
    return {
      category: props.asset
        ? { label: props.asset.category.name, value: props.asset.category.id }
        : { label: "", value: "" },
      asset_name: "",
      product_from_asset: { label: "", value: "" },
      product_name: props.product ? props.product.name : "",
      language: { label: "English (EN)", value: "en" },
      input_customer_ids: [],
      input_user_ids: [],
      input_role_ids: [],
    };
  }, [props]);

  const methodsOfUseForm = useFormWrapper({
    defaultValues,
  });

  const {
    control,
    errors,
    formState,
    handleSubmit,
    register,
    setValue,
    watch,
  } = methodsOfUseForm;

  const categoryValue = watch("category", {
    label: "Technical Data Sheet (TDS)",
    value: "tds",
  });

  useEffect(() => {
    if (props.product && !props.asset && categoryValue?.value) {
      const matched = getActiveGeneratedAssetForCategory(
        templateGeneratedAssets,
        categoryValue.value
      );
      if (matched) {
        setGeneratedAsset(matched);
        setTableData(mapSectionsToTableData(matched.sections));
      } else {
        setGeneratedAsset(undefined);
        setTableData([]);
      }
    }
  }, [
    categoryValue?.value,
    props.product,
    props.asset,
    templateGeneratedAssets,
    mapSectionsToTableData,
  ]);
  const input_role_ids = watch("input_role_ids") || [];
  const input_user_ids = watch("input_user_ids") || [];

  const tableColumns = useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: t("Type"),
        accessorKey: "type",
        cell: ({ getValue }: { getValue: Getter<string> }) => (
          <div>{toTitleCase(getValue())}</div>
        ),
      },
    ],
    [t]
  );

  const onGenerateAsset = useCallback(
    (asset: Assets) => {
      setValue("language", { label: "English (EN)", value: "en" });
      setAsset(asset);
      setAccessibility(asset.visibility);
    },
    [setValue]
  );

  const getProducts = useCallback(
    async (asset: Assets) => {
      const products = (
        props.product
          ? [props.product.id]
          : props.asset
          ? asset.product_ids
          : []
      )
        .map((product_id) => `product_id=${product_id}`)
        .join("&");
      const { data: prods } = await axios.get<{ data: Array<PIMProductBase> }>(
        `${endpoints.v2_tenants_id_pim_products_summary(
          tenant_id
        )}?use_database=true&${products}`
      );
      const prodOptions: OptionType<string>[] =
        prods.data.length === 1
          ? [{ label: prods.data[0].name, value: prods.data[0].id }]
          : prods.data
              .filter(({ status }) => status.includes("staged"))
              .map((data) => ({
                label: data.name,
                value: data.id,
              }));
      setProductOptions(prodOptions);
      if (prodOptions.length === 1) {
        setValue("product_from_asset", prodOptions[0]);
      }
    },
    [props.asset, props.product, setValue, tenant_id]
  );

  const update_timeout_id = (timeout_id: NodeJS.Timeout) => {
    set_poll_timeout(timeout_id);
  };

  const changeAssetAccessibility = (
    newAccessibility: string | React.FormEvent<HTMLSelectElement>
  ) => {
    if (typeof newAccessibility === "string") {
      setAccessibility(newAccessibility as SupportedAssetAccessibility);
    } else if (newAccessibility.currentTarget.value) {
      setAccessibility(
        newAccessibility.currentTarget.value as SupportedAssetAccessibility
      );
    }
  };

  const existingAccessibilityData = useMemo(
    () => ({
      users: getUsersExistingData(props.asset?.users),
      roles: getRolesExistingData(
        props.asset?.allow_all_roles,
        props.asset?.roles
      ),
      customers: getCustomersExistingData(
        props.asset?.allow_all_customers,
        props.asset?.allow_all_distributors,
        props.asset?.customers
      ),
    }),
    [props.asset]
  );

  const onSubmit = async ({
    asset_name,
    language,
    input_customer_ids,
    input_user_ids,
    input_role_ids,
  }: {
    asset_name: string;
    language: OptionType<SupportedLanguage>;
    input_customer_ids: string[];
    input_user_ids: string[];
    input_role_ids: string[];
  }) => {
    if (asset) {
      setIsSubmitting(true);
      const requestBody = {
        name: asset_name,
        asset_type: asset.asset_type,
        category_id: asset.category.id,
        language: language.value,
        is_downloadable: asset.is_downloadable ?? false,
        is_cover_image: asset.is_cover_image ?? false,
        id: asset.id,
        unarchive: true,
        visibility: accessibility,
        ...accessibilityRequestData(
          asset.asset_type,
          accessibility,
          input_customer_ids,
          input_user_ids,
          input_role_ids
        ),
      };
      try {
        let ackedProduct: PIMProductStatusAck | null = null;
        if (!props.asset) {
          const { data } = await updateProductStatus();
          ackedProduct = data;
        }
        const { data: updatedAsset } = await axios.patch<Assets>(
          endpoints.v2_tenants_id_pim_assets_id(tenant_id, asset.id),
          {
            ...requestBody,
            ...(!props.asset
              ? { product_ids: [ackedProduct!.id] }
              : props.product
              ? { product_ids: [props.product.id] }
              : {}),
          }
        );
        notifySuccess(t("Asset successfully edited"));
        setAsset(updatedAsset);
        setAccessibility(updatedAsset.visibility);
        setIsSubmitting(false);
        props.onSuccess(
          !props.asset
            ? ackedProduct?.primary_staged_product_id ?? ackedProduct?.id
            : "",
          true
        );
      } catch (error) {
        const errorMessage = (error as AxiosError)?.response?.data?.message;
        notifyError(
          errorMessage
            ? errorMessage
            : t("There was an error editing the generated asset"),
          {
            error,
          }
        );
        setIsSubmitting(false);
      }
    }
  };

  useEffect(() => {
    if (asset) {
      const assetNameArr = asset.name.split(".");
      const assetName =
        assetNameArr.length > 1
          ? assetNameArr.slice(0, -1).join("")
          : assetNameArr[0];
      setValue("asset_name", assetName);
    }
  }, [asset, setValue]);

  useImperativeHandle(
    ref,
    () => {
      return {
        clear_poll_timeout() {
          clearTimeout(poll_timeout);
        },
      };
    },
    [poll_timeout]
  );

  return (
    <>
      <Form noValidate onSubmit={handleSubmit(onSubmit)}>
        <Controller
          as={SelectBoxV2}
          control={control}
          name="category"
          placeholder={t("Category")}
          options={assetCategories ?? []}
          isDisabled={props.isCategoryDisabled}
          errors={errors}
          formState={formState}
        />
        {!props.asset && (
          <TextField
            name="product_name"
            label={t("Product Name")}
            theref={register()}
            formState={formState}
            disabled={true}
            errors={errors}
            type="text"
          />
        )}
        {props.asset && (
          <Controller
            as={SelectBoxV2}
            control={control}
            name="product_from_asset"
            placeholder={t("Select Product")}
            options={productOptions ?? []}
            rules={{
              validate: (val) =>
                !props.asset
                  ? true
                  : val?.value
                  ? true
                  : strings(t).thisIsARequiredField,
            }}
            errors={errors}
            isDisabled={productOptions?.length === 1}
            formState={formState}
          />
        )}
        <H6Bold>{t("Selected groups & collections")}</H6Bold>
        <ScrollArea>
          <Table
            columns={tableColumns}
            data={tableData}
            isLoading={false}
            error={undefined}
            Placeholder={<TablePlaceholder message={t("No items to show.")} />}
          />
        </ScrollArea>
        {generatedAsset && (
          <GenerateDocument
            asset={asset}
            product_id={
              props.product
                ? props.product.id
                : asset
                ? asset.product_ids[0]
                : ""
            }
            generate_id={generatedAsset.id}
            update_poll_timeout_id={update_timeout_id}
            onGenerate={(asset) => {
              onGenerateAsset(asset);
              notifySuccess(
                props.asset
                  ? t("Generated asset has been updated successfully")
                  : t("Asset has been successfully generated")
              );
            }}
          />
        )}
        <TextField
          name="asset_name"
          label={t("Asset Name")}
          theref={register({ required: strings(t).thisIsARequiredField })}
          formState={formState}
          errors={errors}
          readOnly={!asset}
          type="text"
        />
        <Controller
          as={SelectBoxV2}
          control={control}
          name="language"
          placeholder={t("Language")}
          options={[{ label: "English (EN)", value: "en" }]}
          errors={errors}
          isDisabled={!asset}
          formState={formState}
        />
        {props.product && (
          <DocumentAccessibility
            accessibility={accessibility}
            assetContainerType={assetContainerType}
            methodsOfUseForm={methodsOfUseForm}
            onAccessibilityChange={changeAssetAccessibility}
            existingData={existingAccessibilityData}
          />
        )}
        <ButtonContainer style={{ marginTop: "30px" }}>
          <TertiaryButtonLargeV2
            fullWidth
            disabled={isSubmitting}
            type="button"
            onClick={props.onCancel}
          >
            {t("Cancel")}
          </TertiaryButtonLargeV2>
          <PrimaryButtonLargeV2
            fullWidth
            loading={isSubmitting}
            type="submit"
            disabled={
              !asset ||
              disableAccessibilitySubmit(
                asset?.asset_type ?? "document",
                accessibility,
                input_role_ids,
                input_user_ids
              )
            }
          >
            {t("Save")}
          </PrimaryButtonLargeV2>
        </ButtonContainer>
      </Form>
    </>
  );
});
